import { useAccountState } from '../../../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import {
    Button,
    Checkbox,
    Divider,
    Dropdown,
    Field,
    InfoLabel,
    Input,
    Link,
    makeStyles,
    mergeClasses,
    Option,
    OptionOnSelectData,
    Text,
    tokens,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular, DocumentAddRegular, SaveRegular } from '@fluentui/react-icons';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { BrandCard, Column, Paths, Row } from '../../../common';
import {
    Invoice,
    Case,
    CaseExpense,
    TimeTrackingEntry,
    UserAccount,
    UserRef,
    AppMessage,
    useInitialValues,
} from '../../../../data/types';
import { useToasts } from '../../../toasts/useToasts';
import { useCommonStyles } from '../../../common/Styles/commonStyles';

import { useUtilities } from '../../../../utilities/useUtilities';
import {
    CustomDataTable,
    CustomDataTableColumn,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { AppMessageDisplay, useAppMessageDisplay } from '../../../controls/AppMessageDisplay';
import { useAccess } from '../../../../auth/useAccess';
import { TimeTrackingTabs } from '../CaseTimeTracking/TimeTrackingPage';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { CaseTabs } from '../../Tabs/CaseTabs';
import { useValidation } from '../../../../data/types/validation';
import { PayTerms } from '../CaseEngagementDoc/EngagementEnums';
import { useGuid } from '../../../../utilities/useGuid';

type Installment = {
    id: string;
    invoiceDate: Date;
    amount: string;
    paymentTerms: string;
};

const useStyles = makeStyles({
    tableDisabled: {
        '& *': {
            color: tokens.colorNeutralForegroundDisabled,
        },
    },
});

enum BillingPeriod {
    custom = 'Custom',
}

export type CreateInvoicePageProps = {
    onInvoiceCreated?: (invoice: Invoice) => void;
};

export const CreateInvoicePage = (props: CreateInvoicePageProps) => {
    const { onInvoiceCreated } = props;

    const { caseForm, invoiceService, expenseService, timeTrackingService, caseEngagementService } =
        useCaseServicesContext()!;
    const caseObj = caseForm.values as Case;
    const assignedUsersMap = new Map(caseObj.assignedUsers?.map((user) => [user.userId, user]) ?? []);

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const initValues = useInitialValues();
    const toasts = useToasts();
    const utils = useUtilities();
    const messageDisplay = useAppMessageDisplay();
    const navTo = useNavigate();
    const { validateInvoice } = useValidation();
    const guid = useGuid();

    //State
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [billPeriodFilter, setBillPeriodFilter] = useState<string>();
    const [payTermsFilter, setPayTermsFilter] = useState<string>(PayTerms.uponReceipt);
    const [installments, setInstallments] = useState<Installment[]>([]);

    //Styles
    const commonStyles = useCommonStyles();

    //Form
    const reGenerateInvoiceNumber = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 1000)
            .toString()
            .padStart(3, '0');
        return `INV-${year}${month}${day}-${random}`;
    };
    const initialFormValues = (): Invoice => {
        return {
            ...initValues.invoice(caseObj.caseId, caseObj.fixedAmount),
            invoiceNumber: reGenerateInvoiceNumber(),
        };
    };
    const invoiceForm = useFormik<Invoice>({
        initialValues: initialFormValues(),
        onSubmit: async (values, { resetForm }) => {
            setIsCreating(true);
            try {
                if (caseObj?.isFixedFee) {
                    // Create one invoice per installment
                    const createdInvoices: Invoice[] = [];
                    for (let i = 0; i < installments.length; i++) {
                        const inst = installments[i];
                        const invNumber = `${reGenerateInvoiceNumber()}-${String(i + 1).padStart(2, '0')}`;
                        const instInvoice: Invoice = {
                            ...values,
                            invoiceId: guid.newGuid(),
                            invoiceNumber: invNumber,
                            caseId: caseObj.caseId,
                            invoiceDate: inst.invoiceDate,
                            dueDate: computeDueDate(inst.invoiceDate, inst.paymentTerms),
                            billableTimeEntries: [],
                            expenses: [],
                            fixedAmount: inst.amount,
                            paymentBy: inst.paymentTerms,
                        };
                        await invoiceService.upsertInvoiceAsync(instInvoice, i === installments.length - 1);
                        createdInvoices.push(instInvoice);
                    }

                    if (onInvoiceCreated && createdInvoices.length > 0) {
                        onInvoiceCreated(createdInvoices[0]);
                    }
                } else {
                    const newInvoice: Invoice = {
                        ...values,
                        caseId: caseObj.caseId,
                        billableTimeEntries: fillWithRates(timeTrackingService?.billableTimeEntries?.filter(filterTimeEntries)),
                        expenses: primaryAccount?.account.features?.expenseManagement
                            ? expenseService?.expenses?.filter(filterExpenses)
                            : [],
                        fixedAmount: undefined,
                        paymentBy: payTermsFilter,
                    };

                    await invoiceService.upsertInvoiceAsync(newInvoice, true);
                   
                    if (onInvoiceCreated) {
                        onInvoiceCreated(newInvoice);
                    }
                }

                // Reset form
                resetForm();
                setInstallments([]);
            } catch (error) {
                toasts.notifyError('Error Creating Invoice', `An error occurred while creating the invoice. ${error}`);
            } finally {
                setIsCreating(false);
            }
        },
        validationSchema: validateInvoice.schema,
    });

    //Access
    const installmentsTotal = () => {
        return installments.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
    };
    const installmentsMatchFixedFee = () => {
        const fixed = Number(caseObj?.fixedAmount) || 0;
        if (fixed <= 0) return false;
        // Compare with a small epsilon to avoid floating-point issues
        return Math.abs(installmentsTotal() - fixed) < 0.005;
    };
    const canCreateInvoice = () => {
        const goodToGo = (
            !isCreating &&
            invoiceForm.isValid &&
            !!caseObj?.caseId
        );

        if(caseObj?.isFixedFee) {
            return goodToGo &&
                !!caseObj?.fixedAmount &&
                installments.length > 0 &&
                installments.every((i) => !!i.invoiceDate && Number(i.amount) > 0 && !!i.paymentTerms) &&
                installmentsMatchFixedFee();
        } else {
            return goodToGo && hasBillableRates() &&
            !!invoiceForm.values.billingPeriodStart &&
            !!invoiceForm.values.billingPeriodEnd;
        }
    };
    const canSeeExpenses = () => {
        return primaryAccount?.account.features?.expenseManagement;
    };

    //Filters
    const filterExpenses = (expense: CaseExpense) => {
        return (
            invoiceForm.values.billingPeriodStart &&
            invoiceForm.values.billingPeriodEnd &&
            new Date(expense.date || 0) >= invoiceForm.values.billingPeriodStart &&
            new Date(expense.date || 0) <= invoiceForm.values.billingPeriodEnd &&
            !expense.invoiceId &&
            !!expense.recoverable
        );
    };
    const filterTimeEntries = (entry: TimeTrackingEntry) => {
        return (
            invoiceForm.values.billingPeriodStart &&
            invoiceForm.values.billingPeriodEnd &&
            new Date(entry.date || 0) >= invoiceForm.values.billingPeriodStart &&
            new Date(entry.date || 0) <= invoiceForm.values.billingPeriodEnd &&
            !entry.invoiceId
        );
    };

    //Messages
    const onBillableRatesLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        goToBillableRates();
    };
    const getRatesActionText = () => {
        if (access.timeTracking.canEditBillableRates(caseObj.caseId)) {
            return (
                <Text>
                    Please{' '}
                    <Link onClick={onBillableRatesLinkClick} data-testid='create-invoice-billable-rates-link'>
                        click here
                    </Link>{' '}
                    to enter billable rates for this case.
                </Text>
            );
        } else {
            return <Text>Please contact an admin to enter billable rates for this case.</Text>;
        }
    };
    const noBillableRatesMsg: AppMessage = {
        id: 'noBillableRatesMsg',
        intent: 'warning',
        title: 'Missing Billable Rates',
        content: (
            <Text>
                Your Scope of Work indicates you are billing by the hour. However, not all billable rates have been set.{' '}
                {getRatesActionText()}
            </Text>
        ),
        canDismiss: false,
    };

    //Logic
    const fillWithRates = (timeEntries: TimeTrackingEntry[]) => {
        const filledEntries = timeEntries.map((entry: TimeTrackingEntry) => {
            return {
                ...entry,
                rate: assignedUsersMap.get(entry.userId!)?.clientBillRate,
            } as TimeTrackingEntry;
        });

        return filledEntries;
    };
    const goToBillableRates = () => {
        navTo(Paths.viewEditCase(caseObj.caseId as string), {
            state: { tab: CaseTabs.caseTimeTracking, subTab: TimeTrackingTabs.billableRates },
        });
    };
    const hasBillableRates = () => {
        return caseObj.assignedUsers?.every((aUser) => !!aUser.clientBillRate);
    };
    const checkBillableRates = () => {
        if (!caseObj?.isFixedFee && !hasBillableRates()) {
            messageDisplay.addMessage(noBillableRatesMsg);
        }
    };

    const handleGenerateNumber = () => {
        const invNumber = reGenerateInvoiceNumber();
        invoiceForm.setFieldValue('invoiceNumber', invNumber);
        invoiceForm.validateField('invoiceNumber');
    };
    const setDueDate = (payTerms: string) => {
        if (!!invoiceForm.values.invoiceDate) {
            const due = computeDueDate(invoiceForm.values.invoiceDate, payTerms);
            invoiceForm.setFieldValue('dueDate', due);
        }
    };
    const computeDueDate = (invoiceDate: Date, payTerms: string): Date => {
        const invDate = new Date(invoiceDate);
        const dueDate = new Date(invDate);
        if (payTerms == PayTerms.net15) dueDate.setDate(dueDate.getDate() + 15);
        else if (payTerms == PayTerms.net30) dueDate.setDate(dueDate.getDate() + 30);
        else if (payTerms == PayTerms.net60) dueDate.setDate(dueDate.getDate() + 60);
        else if (payTerms == PayTerms.net90) dueDate.setDate(dueDate.getDate() + 90);
        // uponReceipt / custom: same day as invoice date
        return dueDate;
    };

    //Installments
    const addInstallment = () => {
        const remaining = Math.max(0, (Number(caseObj?.fixedAmount) || 0) - installmentsTotal());
        const nextDate = installments.length
            ? new Date(installments[installments.length - 1].invoiceDate)
            : new Date();
        setInstallments([
            ...installments,
            {
                id: guid.newGuid(),
                invoiceDate: nextDate,
                amount: remaining > 0 ? remaining.toFixed(2) : '',
                paymentTerms: PayTerms.uponReceipt,
            },
        ]);
    };
    const updateInstallment = (id: string, patch: Partial<Installment>) => {
        setInstallments((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    };
    const removeInstallment = (id: string) => {
        setInstallments((prev) => prev.filter((i) => i.id !== id));
    };

    //UI Events
    const onPayTermsChange = (e: any, data: OptionOnSelectData) => {
        e?.stopPropagation?.();
        const payTerms = data.optionText ?? '';
        setPayTermsFilter(payTerms);
        setDueDate(payTerms);
    };
    const onCreateInvoiceClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        invoiceForm.submitForm();
    };

    const onReGenerateInvoiceNumberClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleGenerateNumber();
    };
    const onInvoiceDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('invoiceDate', date || new Date());
    };
    const onInvoiceDateTodayClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        invoiceForm.setFieldValue('invoiceDate', new Date());
    };
    const onBillPeriodOptionSelect = (e: any, data: OptionOnSelectData) => {
        e?.stopPropagation?.();
        setBillPeriodFilter(data.optionText ?? '');
    };
    const onBillingPeriodStartDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('billingPeriodStart', date || new Date());
    };
    const onBillingPeriodEndDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('billingPeriodEnd', date || new Date());
    };
    const onDueDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('dueDate', date || new Date());
    };
    const onDueDateClick = (e: MouseEvent<HTMLElement>) => {
        if (caseEngagementService?.engagementDoc?.paymentBy != PayTerms.custom) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    const onAttachEngagementChange = (e: any, data: any) => {
        e?.stopPropagation?.();
        invoiceForm.setFieldValue('attachEngagement', data.checked);
    };

    const [billMonths, setBillMonths] = useState<Date[]>([]);
    const generateLastYearInMonths = () => {
        const months: Date[] = [];
        const today = new Date();

        for (let i = 6; i >= 0; i--) {
            const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
            months.push(date);
        }

        setBillMonths(months);
    };
    const getMonthOptionFormat = (date: Date) => {
        const dateFormatted = utils.date.formatDate(date, { month: 'long', year: 'numeric' }, true);
        const currentOrLast = utils.date.isLastMonth(date)
            ? ' (Last Month)'
            : utils.date.isCurrentMonth(date)
              ? ' (This Month)'
              : '';

        return `${dateFormatted} ${currentOrLast}`;
    };

    //Effects
    useEffect(() => {
        generateLastYearInMonths();
        checkBillableRates();
    }, []);

    useEffect(() => {
        invoiceForm.resetForm({ values: initialFormValues() });
        setBillPeriodFilter(undefined);
        setPayTermsFilter(PayTerms.uponReceipt);
        setInstallments([]);
    }, [caseObj.caseId]);

    useEffect(() => {
        if (!!billPeriodFilter) {
            const filterDate = billPeriodFilter == BillingPeriod.custom ? new Date() : new Date(billPeriodFilter);
            const firstDayOfMonth = new Date(filterDate.getFullYear(), filterDate.getMonth(), 1);
            const lastDayOfMonth = new Date(filterDate.getFullYear(), filterDate.getMonth() + 1, 0);

            invoiceForm.setFieldValue('billingPeriodStart', firstDayOfMonth);
            invoiceForm.setFieldValue('billingPeriodEnd', lastDayOfMonth);
        }
    }, [billPeriodFilter]);

    useEffect(() => {
        setDueDate(payTermsFilter);
    }, [invoiceForm.values.invoiceDate]);

    return (
        <Column flex='1'>
            <BrandCard
                flex='1'
                title='Create New Invoice'
                headerIcon={<DocumentAddRegular />}
                action={
                    <>
                        <Button
                            className={commonStyles.button.root}
                            icon={<SaveRegular />}
                            appearance='primary'
                            onClick={onCreateInvoiceClick}
                            disabled={!canCreateInvoice()}
                            data-testid='create-invoice-submit-button'
                        >
                            {isCreating ? 'Creating...' : 'Create Invoice'}
                        </Button>
                    </>
                }
            >
                <Column scroll>
                    {/* <Text size={200}>{JSON.stringify(invoiceForm.errors)}</Text> */}
                    <AppMessageDisplay messages={messageDisplay.messages} onDismiss={messageDisplay.removeMessage} />
                    <Row gap='large'>
                        <Column flex='2' gap='medium' padding='pl-3'>
                            <Text size={400} italic weight='semibold'>
                                Invoice Info
                            </Text>
                            <Column gap='medium' padding='pl-3'>
                                <Field label='Invoice Number' required validationMessage={invoiceForm.errors.invoiceNumber}>
                                    <Row>
                                        <Input
                                            name='invoiceNumber'
                                            value={invoiceForm.values.invoiceNumber}
                                            onChange={invoiceForm.handleChange}
                                            placeholder='Enter invoice number'
                                            data-testid='create-invoice-number-input'
                                        />
                                        <Button
                                            appearance='secondary'
                                            onClick={onReGenerateInvoiceNumberClick}
                                            data-testid='create-invoice-regenerate-number-button'
                                        >
                                            Re-Generate
                                        </Button>
                                    </Row>
                                </Field>
                                <Field label='Invoice Date' required>
                                    <Row>
                                        <DatePicker
                                            name='invoiceDate'
                                            value={invoiceForm.values.invoiceDate}
                                            onSelectDate={onInvoiceDateSelect}
                                            placeholder='Select invoice date'
                                            minDate={caseObj?.retainedDate ? new Date(caseObj.retainedDate) : undefined}
                                            data-testid='create-invoice-date-picker'
                                        />
                                        <Button
                                            appearance='secondary'
                                            onClick={onInvoiceDateTodayClick}
                                            data-testid='create-invoice-today-button'
                                        >
                                            Today
                                        </Button>
                                    </Row>
                                </Field>
                            </Column>
                        </Column>
                        
                        <Column flex='3' gap='medium'>
                            <Text size={400} italic weight='semibold'>
                                Billing Period and Terms
                            </Text>
                            <Column gap='medium' padding='pl-3'>
                                {!caseObj?.isFixedFee && (
                                    <Row noWrap gap='medium'>
                                        <Column width='auto'>
                                            <Field label='Billing Period' style={{ alignSelf: 'start' }}>
                                                <Row>
                                                    <Column>
                                                        <Dropdown
                                                            onOptionSelect={onBillPeriodOptionSelect}
                                                            data-testid='create-invoice-billing-period-dropdown'
                                                        >
                                                            {billMonths.map((month: Date) => (
                                                                <Option
                                                                    key={month.toDateString()}
                                                                    value={month.toDateString()}
                                                                    data-testid={`create-invoice-billing-period-option-${month.toDateString()}`}
                                                                >
                                                                    {getMonthOptionFormat(month)}
                                                                </Option>
                                                            ))}
                                                            <Option
                                                                key={BillingPeriod.custom}
                                                                value={BillingPeriod.custom}
                                                                data-testid='create-invoice-billing-period-option-custom'
                                                            >
                                                                {BillingPeriod.custom}
                                                            </Option>
                                                        </Dropdown>
                                                    </Column>
                                                    <Column vAlign='middle'>
                                                        {billPeriodFilter != BillingPeriod.custom && (
                                                            <>
                                                                <Text size={400} weight='semibold'>
                                                                    Billing Period:{' '}
                                                                    {utils.date.formatDate(
                                                                        invoiceForm.values.billingPeriodStart,
                                                                        {
                                                                            month: 'long',
                                                                            year: 'numeric',
                                                                        },
                                                                        true,
                                                                    )}
                                                                </Text>
                                                            </>
                                                        )}
                                                    </Column>
                                                </Row>
                                            </Field>
                                        </Column>
                                        <Column width='auto' vAlign='bottom'>
                                            {billPeriodFilter == BillingPeriod.custom && (
                                                <Field label='Billing Period Start' required style={{ alignSelf: 'start' }}>
                                                    <DatePicker
                                                        name='billingPeriodStart'
                                                        value={invoiceForm.values.billingPeriodStart}
                                                        onSelectDate={onBillingPeriodStartDateSelect}
                                                        placeholder='Select billing period start'
                                                        data-testid='create-invoice-billing-period-start-picker'
                                                    />
                                                </Field>
                                            )}
                                        </Column>
                                        <Column width='auto'>
                                            {billPeriodFilter == BillingPeriod.custom && (
                                                <Field label='Billing Period End' required style={{ alignSelf: 'start' }}>
                                                    <DatePicker
                                                        name='billingPeriodEnd'
                                                        value={invoiceForm.values.billingPeriodEnd}
                                                        onSelectDate={onBillingPeriodEndDateSelect}
                                                        placeholder='Select billing period end'
                                                        data-testid='create-invoice-billing-period-end-picker'
                                                    />
                                                </Field>
                                            )}
                                        </Column>
                                    </Row>
                                )}
                                {caseObj?.isFixedFee && (
                                    <Column gap='small'>
                                        <Row justifyContent='space-between' noWrap>
                                            <InfoLabel info={<>Fixed fee specified in the Case Info</>}>
                                                <Text size={400} weight='semibold'>
                                                    Fixed Fee: {utils.formatCurrency(caseObj?.fixedAmount)}
                                                </Text>
                                            </InfoLabel>
                                            <Text
                                                size={300}
                                                weight='semibold'
                                                style={{
                                                    color: installmentsMatchFixedFee()
                                                        ? tokens.colorPaletteGreenForeground1
                                                        : tokens.colorPaletteRedForeground1,
                                                }}
                                            >
                                                Installments Total: {utils.formatCurrency(installmentsTotal())} /{' '}
                                                {utils.formatCurrency(caseObj?.fixedAmount)}
                                            </Text>
                                        </Row>
                                        <Row justifyContent='space-between'>
                                            <Text size={300} italic>
                                                Add one or more installments. Each installment will be created as a
                                                separate invoice.
                                            </Text>
                                            <Button
                                                appearance='secondary'
                                                icon={<AddRegular />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    addInstallment();
                                                }}
                                                data-testid='create-invoice-add-installment-button'
                                            >
                                                Add Installment
                                            </Button>
                                        </Row>
                                        {installments.map((inst, idx) => (
                                            <Row key={inst.id} gap='medium' noWrap>
                                                <Column width='auto'>
                                                    <Field label={idx === 0 ? 'Invoice Date' : undefined} required>
                                                        <DatePicker
                                                            value={inst.invoiceDate}
                                                            onSelectDate={(date) =>
                                                                updateInstallment(inst.id, {
                                                                    invoiceDate: date || new Date(),
                                                                })
                                                            }
                                                            placeholder='Select invoice date'
                                                            minDate={
                                                                caseObj?.retainedDate
                                                                    ? new Date(caseObj.retainedDate)
                                                                    : undefined
                                                            }
                                                            data-testid={`create-invoice-installment-date-${idx}`}
                                                        />
                                                    </Field>
                                                </Column>
                                                <Column width='auto'>
                                                    <Field label={idx === 0 ? 'Amount' : undefined} required>
                                                        <Input
                                                            type='number'
                                                            value={inst.amount}
                                                            onChange={(_, data) =>
                                                                updateInstallment(inst.id, { amount: data.value })
                                                            }
                                                            placeholder='0.00'
                                                            data-testid={`create-invoice-installment-amount-${idx}`}
                                                        />
                                                    </Field>
                                                </Column>
                                                <Column width='auto'>
                                                    <Field label={idx === 0 ? 'Payment Terms' : undefined} required>
                                                        <Dropdown
                                                            style={{ minWidth: '150px' }}
                                                            value={inst.paymentTerms}
                                                            selectedOptions={[inst.paymentTerms]}
                                                            onOptionSelect={(e, data) => {
                                                                e?.stopPropagation?.();
                                                                updateInstallment(inst.id, {
                                                                    paymentTerms: data.optionText ?? PayTerms.uponReceipt,
                                                                });
                                                            }}
                                                            data-testid={`create-invoice-installment-terms-${idx}`}
                                                        >
                                                            {Object.values(PayTerms).map((option) => (
                                                                <Option key={option} value={option}>
                                                                    {option}
                                                                </Option>
                                                            ))}
                                                        </Dropdown>
                                                    </Field>
                                                </Column>
                                                <Column width='auto' vAlign='bottom'>
                                                    <Button
                                                        appearance='subtle'
                                                        icon={<DeleteRegular />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeInstallment(inst.id);
                                                        }}
                                                        aria-label='Remove installment'
                                                        data-testid={`create-invoice-installment-remove-${idx}`}
                                                    />
                                                </Column>
                                            </Row>
                                        ))}
                                    </Column>
                                )}
                                <Row>
                                    <Column width='auto' vAlign='middle'>
                                        <Field label='Payment Terms' style={{ alignSelf: 'start' }}>
                                            <Dropdown
                                                style={{ minWidth: '150px' }}
                                                value={payTermsFilter}
                                                onOptionSelect={onPayTermsChange}
                                                data-testid='create-invoice-payment-terms-dropdown'
                                                disabled={caseObj?.isFixedFee}
                                            >
                                                {Object.values(PayTerms).map((option) => (
                                                    <Option
                                                        key={option}
                                                        value={option}
                                                        data-testid={`create-invoice-payment-terms-option-${option}`}
                                                    >
                                                        {option}
                                                    </Option>
                                                ))}
                                            </Dropdown>
                                        </Field>
                                    </Column>
                                    <Column width='auto' vAlign='middle'>
                                        <Field
                                            label='Due Date'
                                            required={caseEngagementService?.engagementDoc?.paymentBy == PayTerms.custom}
                                        >
                                            {payTermsFilter == PayTerms.custom ? (
                                                <DatePicker
                                                    name='dueDate'
                                                    value={invoiceForm.values.dueDate}
                                                    onSelectDate={onDueDateSelect}
                                                    placeholder='Select due date'
                                                    readOnly={
                                                        caseEngagementService?.engagementDoc?.paymentBy != PayTerms.custom
                                                    }
                                                    onClick={onDueDateClick}
                                                    data-testid='create-invoice-due-date-picker'
                                                />
                                            ) : (
                                                <Text size={400} weight='semibold'>
                                                    {utils.date.formatDate(
                                                        invoiceForm.values.dueDate,
                                                        {
                                                            weekday: 'short',
                                                            month: 'short',
                                                            day: '2-digit',
                                                            year: 'numeric',
                                                        },
                                                        true,
                                                    )}
                                                </Text>
                                            )}
                                        </Field>
                                    </Column>
                                </Row>

                                <Field label='Case Engagement'>
                                    <Checkbox
                                        checked={invoiceForm.values.attachEngagement}
                                        onChange={onAttachEngagementChange}
                                        label='Attach Engagement Document'
                                        data-testid='create-invoice-attach-engagement-checkbox'
                                    />
                                </Field>
                            </Column>
                        </Column>
                    </Row>

                    <Column padding='px-2 py-2' borderRadius='8px' subtleBorder minHeight='fit-content'>
                        <Text size={400} italic weight='semibold'>
                            Billable Time
                        </Text>
                        <InvoiceBillableTimeEntryTable
                            disabled={caseObj?.isFixedFee}
                            entries={timeTrackingService?.billableTimeEntries?.filter(filterTimeEntries)}
                            assignedUsers={caseObj.assignedUsers}
                        />
                    </Column>

                    {canSeeExpenses() && (
                        <Column padding='px-2 py-2' borderRadius='8px' subtleBorder minHeight='fit-content'>
                            <Text size={400} italic weight='semibold'>
                                Expenses
                            </Text>
                            <InvoiceExpensesTable
                                disabled={caseObj?.isFixedFee}
                                expenses={expenseService?.expenses?.filter(filterExpenses)}
                            />
                        </Column>
                    )}
                </Column>
            </BrandCard>
        </Column>
    );
};

enum BillableColumns {
    date = 'date',
    user = 'user',
    description = 'descrip',
    hours = 'hours',
    rate = 'rate',
    total = 'total',
}
type InvoiceBillableTimeEntryTableProps = {
    entries?: TimeTrackingEntry[];
    assignedUsers?: UserRef[];
    disabled?: boolean;
};
export const InvoiceBillableTimeEntryTable = (props: InvoiceBillableTimeEntryTableProps) => {
    const { entries, assignedUsers, disabled = false } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { accountUsers } = (primaryAccount as UserAccount) ?? {};
    const utils = useUtilities();

    //Styles
    const styles = useStyles();
    const tableDivClassName = mergeClasses(disabled && styles.tableDisabled);

    const getUserName = (userId: string) => {
        const user = accountUsers.find((user) => user.userId === userId || user.username === userId);
        return user?.name || user?.username;
    };
    const getUserRate = (userId: string) => {
        const user = assignedUsers?.find((user) => user.userId === userId);
        return user ? user.hourlyRate : '0';
    };

    const columns: CustomDataTableColumn<TimeTrackingEntry>[] = [
        {
            id: BillableColumns.date,
            compare: (a, b) => {
                const aDate = new Date(a.date || 0).valueOf();
                const bDate = new Date(b.date || 0).valueOf();
                return aDate - bDate;
            },
            header: 'Date',
            renderCell: (entry) => {
                return <>{utils.date.formatDate(entry.date, undefined, true)}</>;
            },
            defaultWidth: 100,
            minWidth: 100,
        },
        {
            id: BillableColumns.user,
            compare: (a, b) => {
                return a.username?.localeCompare(b.username || '') || 0;
            },
            header: 'User',
            renderCell: (entry) => {
                return <>{getUserName(entry.userId as string)}</>;
            },
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: BillableColumns.description,
            header: 'Description',
            renderCell: (entry) => {
                return <>{entry.description}</>;
            },
            defaultWidth: 350,
            minWidth: 100,
        },
        {
            id: BillableColumns.hours,
            compare: (a, b) => {
                return Number(a) - Number(b);
            },
            header: 'Hours',
            renderCell: (entry) => {
                return <>{Number(entry.hours).toFixed(2)}</>;
            },
            defaultWidth: 100,
            minWidth: 100,
        },
        {
            id: BillableColumns.rate,
            compare: (a, b) => {
                return Number(a) - Number(b);
            },
            header: 'Rate',
            renderCell: (entry) => {
                return (
                    <>
                        {utils.formatCurrency(getUserRate(entry.userId as string))}
                    </>
                );
            },
            defaultWidth: 100,
            minWidth: 100,
        },
        {
            id: BillableColumns.total,
            compare: (a, b) => {
                return Number(a) - Number(b);
            },
            header: 'Total',
            renderCell: (entry) => {
                return (
                    <>
                        {utils.formatCurrency(Number(entry.hours) * Number(getUserRate(entry.userId as string)))}
                    </>
                );
            },
            defaultWidth: 100,
            minWidth: 100,
        },
    ];

    return (
        <div className={tableDivClassName}>
            <CustomDataTable<TimeTrackingEntry>
                items={entries || []}
                rowId='entryId'
                sortable={false}
                columns={columns}
                select={false}
            />
        </div>
    );
};

enum ExpenseColumns {
    date = 'date',
    type = 'type',
    description = 'descrip',
    user = 'user',
    amount = 'amount',
}
type InvoiceExpensesTableProps = {
    expenses?: CaseExpense[];
    disabled?: boolean;
};
export const InvoiceExpensesTable = (props: InvoiceExpensesTableProps) => {
    const { expenses, disabled = false } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { accountUsers } = (primaryAccount as UserAccount) ?? {};
    const utils = useUtilities();

    //Styles
    const styles = useStyles();
    const tableDivClassName = mergeClasses(disabled && styles.tableDisabled);

    const getUserName = (username: string) => {
        const user = accountUsers.find((user) => user.username === username);
        return user ? user.name : username;
    };

    const columns: CustomDataTableColumn<CaseExpense>[] = [
        {
            id: ExpenseColumns.date,
            compare: (a, b) => {
                const aDate = new Date(a.date || 0).valueOf();
                const bDate = new Date(b.date || 0).valueOf();
                return aDate - bDate;
            },
            header: 'Date',
            renderCell: (expense) => {
                return <>{utils.date.formatDate(expense.date, undefined, true)}</>;
            },
            defaultWidth: 100,
            minWidth: 100,
        },
        {
            id: ExpenseColumns.type,
            compare: (a, b) => {
                return a.type?.localeCompare(b.type || '') || 0;
            },
            header: 'Expense Type',
            renderCell: (expense) => {
                return <>{expense.type}</>;
            },
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ExpenseColumns.description,
            header: 'Description',
            renderCell: (expense) => {
                return <>{expense.text}</>;
            },
            defaultWidth: 350,
            minWidth: 100,
        },
        {
            id: ExpenseColumns.user,
            compare: (a, b) => {
                return a.username?.localeCompare(b.username || '') || 0;
            },
            header: 'User',
            renderCell: (entry) => {
                return <>{getUserName(entry.username as string)}</>;
            },
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ExpenseColumns.amount,
            compare: (a, b) => {
                return Number(a.amount) - Number(b.amount);
            },
            header: 'Amount',
            renderCell: (expense) => {
                return <>{utils.formatCurrency(Number(expense.amount))}</>;
            },
            defaultWidth: 100,
            minWidth: 100,
        },
    ];

    return (
        <div className={tableDivClassName}>
            <CustomDataTable<CaseExpense>
                items={expenses || []}
                rowId='expenseId'
                sortable={false}
                columns={columns}
                select={false}
            />
        </div>
    );
};
